import React, { useState } from "react";
import { Copy, Loader2, QrCode, Check, Zap, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { parseCurrencyInput, formatBRL } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

export default function GeneratePix() {
  const [value, setValue] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [description, setDescription] = useState("Consultoria Online Marketing");
  const [charge, setCharge] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [marking, setMarking] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const reais = parseCurrencyInput(value);
    if (reais <= 0) {
      toast.error("Informe um valor válido");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/charges", {
        value_reais: reais,
        description,
        customer_name: customerName || null,
      });
      setCharge(res.data);
      toast.success("Cobrança PIX gerada!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar cobrança");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!charge?.br_code) return;
    try {
      await navigator.clipboard.writeText(charge.br_code);
      setCopied(true);
      toast.success("Código PIX copiado!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const reset = () => {
    setCharge(null);
    setValue("");
    setCustomerName("");
  };

  const simulatePayment = async () => {
    if (!charge) return;
    setMarking(true);
    try {
      await api.post(`/charges/${charge.id}/simulate-paid`, {});
      toast.success("Pagamento simulado com sucesso. Recibo pronto em Vendas.");
      setCharge((c) => (c ? { ...c, status: "PAID" } : c));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao simular pagamento");
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="generate-pix-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">
          Nova cobrança
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-bold">Gerar PIX</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Crie uma cobrança PIX instantânea via Woovi. Nome completo, CPF e horário do pagador são salvos automaticamente.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="bg-card border-border" data-testid="pix-form-card">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary" />
              Dados da cobrança
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Valor (R$)
                </Label>
                <Input
                  data-testid="pix-value-input"
                  placeholder="0,00"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  inputMode="decimal"
                  className="h-14 text-2xl font-display font-bold bg-secondary/40 focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Nome do cliente (opcional)
                </Label>
                <Input
                  data-testid="pix-customer-input"
                  placeholder="Ex: João Silva"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="h-11 bg-secondary/40 focus-visible:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Descrição
                </Label>
                <Input
                  data-testid="pix-description-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="h-11 bg-secondary/40 focus-visible:ring-primary"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="pix-generate-btn"
                className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Gerando...
                  </>
                ) : (
                  <>
                    <QrCode className="w-4 h-4 mr-2" /> Gerar cobrança PIX
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card
          className={
            "border-border " + (charge ? "bg-primary/5 border-primary/30 pix-glow" : "bg-card")
          }
          data-testid="pix-result-card"
        >
          <CardHeader>
            <CardTitle className="font-display flex items-center justify-between">
              <span>QR Code & Copia-e-cola</span>
              {charge && (
                <Badge
                  variant="outline"
                  className={
                    charge.status === "PAID"
                      ? "border-primary/40 text-primary bg-primary/10"
                      : "border-amber-500/40 text-amber-400 bg-amber-500/10"
                  }
                  data-testid="charge-status-badge"
                >
                  {charge.status === "PAID" ? "PAGO" : "Aguardando pagamento"}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!charge ? (
              <div className="py-16 text-center text-muted-foreground text-sm">
                O QR Code e o código PIX aparecerão aqui após você gerar a cobrança.
              </div>
            ) : (
              <div className="space-y-5" data-testid="charge-result">
                <div className="grid place-items-center">
                  {charge.qr_code_data_url && (
                    <div className="p-4 bg-white rounded-xl">
                      <img
                        src={charge.qr_code_data_url}
                        alt="QR Code PIX"
                        className="w-56 h-56"
                        data-testid="qr-code-img"
                      />
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Valor</p>
                  <p className="font-display font-bold text-3xl font-mono-num text-primary">
                    {formatBRL(charge.value)}
                  </p>
                </div>
                <Separator />
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Código copia-e-cola
                  </Label>
                  <div className="mt-2 flex gap-2">
                    <div className="flex-1 bg-secondary/60 border border-border rounded-lg p-3 text-xs font-mono break-all max-h-24 overflow-auto" data-testid="br-code-text">
                      {charge.br_code}
                    </div>
                    <Button
                      onClick={copy}
                      variant="outline"
                      className="shrink-0"
                      data-testid="copy-brcode-btn"
                    >
                      {copied ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    onClick={reset}
                    className="flex-1"
                    data-testid="new-charge-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Nova cobrança
                  </Button>
                  {charge.simulation && charge.status !== "PAID" && (
                    <Button
                      onClick={simulatePayment}
                      disabled={marking}
                      className="flex-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                      data-testid="simulate-paid-btn"
                    >
                      {marking ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Simular pagamento"
                      )}
                    </Button>
                  )}
                </div>
                {charge.simulation && (
                  <p className="text-xs text-muted-foreground text-center">
                    Modo simulação: enquanto as credenciais Woovi não estão ativas, você pode simular um pagamento para testar o fluxo e gerar o recibo.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
